import { useState, useEffect } from 'react';
import { getDiscountCodes, createDiscountCode, updateDiscountCode, deleteDiscountCode, generateCode, getAdminCampaigns } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Wand2, Copy, CheckCircle2, Clock, XCircle, Ban, Tag } from 'lucide-react';

const statusCfg = {
  active: { label: 'Attivo', color: '#10B981', bg: 'rgba(16,185,129,0.1)' },
  scheduled: { label: 'Programmato', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
  expired: { label: 'Scaduto', color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
  disabled: { label: 'Disattivato', color: 'var(--text-muted)', bg: 'rgba(148,163,184,0.1)' },
  exhausted: { label: 'Esaurito', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
};

const emptyForm = {
  code: '', campaign_id: '', discount_type: 'percentage', discount_value: 0,
  valid_from: '', valid_until: '', max_uses: 0, is_active: true, description: ''
};

export default function AdminDiscountCodes() {
  const [codes, setCodes] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [c, camps] = await Promise.all([getDiscountCodes(), getAdminCampaigns()]);
      setCodes(c.data);
      setCampaigns(camps.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openNew = async () => {
    setEditingId(null);
    try {
      const res = await generateCode();
      setForm({ ...emptyForm, code: res.data.code });
    } catch {
      setForm({ ...emptyForm });
    }
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.code_id);
    setForm({
      code: c.code, campaign_id: c.campaign_id || '', discount_type: c.discount_type || 'percentage',
      discount_value: c.discount_value || 0, valid_from: c.valid_from ? c.valid_from.slice(0, 16) : '',
      valid_until: c.valid_until ? c.valid_until.slice(0, 16) : '', max_uses: c.max_uses || 0,
      is_active: c.is_active !== false, description: c.description || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.code) { toast.error('Il codice e\' obbligatorio'); return; }
    setSaving(true);
    try {
      const data = { ...form, discount_value: parseFloat(form.discount_value) || 0, max_uses: parseInt(form.max_uses) || 0 };
      if (data.valid_from) data.valid_from = new Date(data.valid_from).toISOString();
      if (data.valid_until) data.valid_until = new Date(data.valid_until).toISOString();
      if (editingId) {
        await updateDiscountCode(editingId, data);
        toast.success('Codice aggiornato');
      } else {
        await createDiscountCode(data);
        toast.success('Codice creato');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo codice sconto?')) return;
    try {
      await deleteDiscountCode(id);
      toast.success('Codice eliminato');
      load();
    } catch { toast.error('Errore nell\'eliminazione'); }
  };

  const handleGenerate = async () => {
    try {
      const res = await generateCode();
      setField('code', res.data.code);
      toast.success('Codice generato!');
    } catch {}
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Codice copiato!');
  };

  const getCampaignTitle = (cid) => {
    const c = campaigns.find(x => x.campaign_id === cid);
    return c ? c.title : 'Tutti i prodotti';
  };

  const stats = {
    total: codes.length,
    active: codes.filter(c => c.status === 'active').length,
    totalUses: codes.reduce((s, c) => s + (c.current_uses || 0), 0),
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-discount-codes">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl tracking-tight" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Codici Sconto</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Genera e gestisci i codici promozionali</p>
        </div>
        <Button onClick={openNew} data-testid="create-code-btn"
          className="rounded-sm text-sm" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}>
          <Plus className="w-4 h-4 mr-1" /> Nuovo Codice
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Codici Totali', value: stats.total },
          { label: 'Attivi Ora', value: stats.active },
          { label: 'Utilizzi Totali', value: stats.totalUses },
        ].map(({ label, value }) => (
          <Card key={label} className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }}>
            <CardContent className="p-4 text-center">
              <p className="text-xl font-medium" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{value}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Codes Table */}
      <Card className="border rounded-sm overflow-hidden" style={{ borderColor: 'var(--border-light)' }} data-testid="codes-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ fontFamily: 'var(--font-body)' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-warm)', borderBottom: '1px solid var(--border-light)' }}>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Codice</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Sconto</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Prodotto</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Validita</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Utilizzi</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Stato</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}></th>
                </tr>
              </thead>
              <tbody>
                {codes.map(c => {
                  const sc = statusCfg[c.status] || statusCfg.disabled;
                  return (
                    <tr key={c.code_id} className="border-b last:border-b-0" style={{ borderColor: 'var(--border-light)' }}
                      data-testid={`code-row-${c.code_id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium px-2 py-0.5 rounded-sm" style={{ fontFamily: 'var(--font-mono)', backgroundColor: 'var(--bg-warm)', color: 'var(--text-primary)' }}>
                            {c.code}
                          </span>
                          <button onClick={() => copyCode(c.code)} className="p-0.5 rounded hover:bg-gray-100">
                            <Copy className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                          </button>
                        </div>
                        {c.description && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{c.description}</p>}
                      </td>
                      <td className="p-3">
                        <span className="text-sm font-medium" style={{ color: 'var(--accent)' }}>
                          {c.discount_type === 'percentage' ? `${c.discount_value}%` : `${c.discount_value} EUR`}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {c.campaign_id ? getCampaignTitle(c.campaign_id) : 'Tutti'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                          {c.valid_from ? new Date(c.valid_from).toLocaleDateString('it-IT') : '—'} → {c.valid_until ? new Date(c.valid_until).toLocaleDateString('it-IT') : '—'}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-sm" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                          {c.current_uses || 0}{c.max_uses > 0 ? ` / ${c.max_uses}` : ''}
                        </span>
                      </td>
                      <td className="p-3">
                        <Badge className="text-xs rounded-sm" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-code-${c.code_id}`}>
                            <Pencil className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(c.code_id)} data-testid={`delete-code-${c.code_id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {codes.length === 0 && (
              <div className="text-center py-12">
                <Tag className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Nessun codice sconto creato</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="code-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-heading)' }}>
              {editingId ? 'Modifica Codice' : 'Nuovo Codice Sconto'}
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {editingId ? 'Aggiorna i dettagli del codice' : 'Genera un nuovo codice promozionale'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs">Codice</Label>
              <div className="flex gap-2 mt-1">
                <Input value={form.code} onChange={e => setField('code', e.target.value.toUpperCase())}
                  placeholder="ES. ESTATE20" className="text-sm font-mono" style={{ fontFamily: 'var(--font-mono)' }}
                  data-testid="code-input" />
                <Button variant="outline" size="sm" onClick={handleGenerate} className="text-xs whitespace-nowrap" data-testid="generate-code-btn">
                  <Wand2 className="w-3.5 h-3.5 mr-1" /> Genera
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Descrizione (opzionale)</Label>
              <Input value={form.description} onChange={e => setField('description', e.target.value)}
                placeholder="es. Sconto lancio estate" className="mt-1 text-sm" data-testid="code-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tipo Sconto</Label>
                <select value={form.discount_type} onChange={e => setField('discount_type', e.target.value)}
                  className="mt-1 w-full h-9 rounded-sm border text-sm px-2" style={{ borderColor: 'var(--border-light)' }}
                  data-testid="code-discount-type">
                  <option value="percentage">Percentuale (%)</option>
                  <option value="fixed">Fisso (EUR)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Valore</Label>
                <Input type="number" step="0.01" value={form.discount_value} onChange={e => setField('discount_value', e.target.value)}
                  className="mt-1 text-sm" data-testid="code-discount-value" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Prodotto (vuoto = tutti)</Label>
              <select value={form.campaign_id} onChange={e => setField('campaign_id', e.target.value)}
                className="mt-1 w-full h-9 rounded-sm border text-sm px-2" style={{ borderColor: 'var(--border-light)' }}
                data-testid="code-campaign">
                <option value="">Tutti i prodotti</option>
                {campaigns.map(c => (
                  <option key={c.campaign_id} value={c.campaign_id}>{c.title} ({c.month_key})</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Valido da</Label>
                <Input type="datetime-local" value={form.valid_from} onChange={e => setField('valid_from', e.target.value)}
                  className="mt-1 text-sm" data-testid="code-valid-from" />
              </div>
              <div>
                <Label className="text-xs">Valido fino a</Label>
                <Input type="datetime-local" value={form.valid_until} onChange={e => setField('valid_until', e.target.value)}
                  className="mt-1 text-sm" data-testid="code-valid-until" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Utilizzi massimi (0 = illimitati)</Label>
              <Input type="number" min={0} value={form.max_uses} onChange={e => setField('max_uses', e.target.value)}
                className="mt-1 text-sm" data-testid="code-max-uses" />
            </div>
            <div className="flex items-center gap-3 py-1">
              <Switch checked={form.is_active} onCheckedChange={v => setField('is_active', v)} data-testid="code-active-switch" />
              <Label className="text-sm">Codice attivo</Label>
            </div>
            <Button onClick={handleSave} disabled={saving} data-testid="save-code-btn"
              className="w-full rounded-sm" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}>
              {saving ? 'Salvataggio...' : editingId ? 'Aggiorna' : 'Crea Codice'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
